import {useEffect, useRef, useState} from "react";
import dayjs from "dayjs";
import {IProtocolStep, IProtocolStepKind, IProtocolStepStatus, TProtocolStepId} from "@/protocols/types/protocol.ts";
import {DeepPartial} from "@/utils/types/DeepPartial.ts";
import {CountdownState} from "@/protocols/studies/components/CountdownDisplay";

interface UseCountdownControllerProps {
    orderedSteps: Array<IProtocolStep>;
    timezone: string;
    onStepChange: (stepId: TProtocolStepId, updates: DeepPartial<IProtocolStep>) => void;
}

interface UseCountdownControllerResult {
    countDownState: CountdownState;
    startCountdown: (step: IProtocolStep, duration: number, description: string) => void;
    stopCountdown: (capturedState: CountdownState) => void;
    cancelCountdown: () => void;
}

const initialState: CountdownState = {
    counting: false,
    counter: 0,
    showHours: false,
    direction: "up",
    description: "",
};

/**
 * Owns countdown timing and step completion/cancel persistence independent of FocusModeView mount state.
 */
export const useCountdownController = ({
    orderedSteps,
    timezone,
    onStepChange,
}: UseCountdownControllerProps): UseCountdownControllerResult => {
    const [countDownState, setCountDownState] = useState<CountdownState>(initialState);
    const countDownStateRef = useRef(countDownState);
    countDownStateRef.current = countDownState;

    const orderedStepsRef = useRef(orderedSteps);
    orderedStepsRef.current = orderedSteps;

    const timezoneRef = useRef(timezone);
    timezoneRef.current = timezone;

    const onStepChangeRef = useRef(onStepChange);
    onStepChangeRef.current = onStepChange;

    const capturedStepIdRef = useRef<TProtocolStepId | null>(null);
    const previousStartTimeRef = useRef<IProtocolStep["startTime"]>(undefined);

    const clearCountdownState = () => {
        setCountDownState(initialState);
        capturedStepIdRef.current = null;
        previousStartTimeRef.current = undefined;
    };

    const getStepDuration = (targetStep: IProtocolStep): number | undefined => {
        if ("duration" in targetStep) {
            return targetStep.duration;
        }
        if (targetStep.kind === IProtocolStepKind.STIM_EVENT) {
            return targetStep.stimMetadata?.treatmentDurationSec;
        }
        return undefined;
    };

    const completeCountdownForStep = (targetStep: IProtocolStep, remainingCounter: number) => {
        const stepDuration = getStepDuration(targetStep);
        if (!stepDuration) {
            console.log(`Step id:${targetStep.id} does not have duration or stimMetadata`);
            clearCountdownState();
            return;
        }

        const endTime = dayjs().tz(timezoneRef.current, false);
        const newDuration = stepDuration - remainingCounter;

        if (targetStep.kind === IProtocolStepKind.STIM_EVENT) {
            onStepChangeRef.current(targetStep.id, {
                status: IProtocolStepStatus.COMPLETED,
                stimMetadata: {treatmentDurationSec: newDuration},
                endTime,
            });
        } else if ("duration" in targetStep) {
            onStepChangeRef.current(targetStep.id, {
                status: IProtocolStepStatus.COMPLETED,
                duration: newDuration,
                endTime,
            });
        } else {
            onStepChangeRef.current(targetStep.id, {endTime});
        }

        clearCountdownState();
    };

    const handleCountdownComplete = () => {
        const targetStepId = capturedStepIdRef.current;
        if (!targetStepId) {
            console.log(`Missing captured step id in ${JSON.stringify(countDownStateRef.current)}`);
            clearCountdownState();
            return;
        }

        const targetStep = orderedStepsRef.current.find((s) => s.id === targetStepId);
        if (!targetStep) {
            console.log(`Failed to find protocol step id:${targetStepId}`);
            clearCountdownState();
            return;
        }

        completeCountdownForStep(targetStep, countDownStateRef.current.counter);
    };

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            const currentState = countDownStateRef.current;
            if (!currentState.counting) return;

            const newCounter = currentState.counter - 1;
            if (newCounter < 0) return;

            if (newCounter === 0) {
                countDownStateRef.current = {...currentState, counter: 0, counting: false};
                setCountDownState({...currentState, counter: 0, counting: false});
                handleCountdownComplete();
            } else {
                countDownStateRef.current = {...currentState, counter: newCounter};
                setCountDownState({...currentState, counter: newCounter});
            }
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, []);

    const startCountdown = (step: IProtocolStep, duration: number, description: string) => {
        capturedStepIdRef.current = step.id;
        previousStartTimeRef.current = step.startTime;
        setCountDownState({
            counting: true,
            counter: duration,
            showHours: duration >= 3600,
            direction: "up",
            description,
        });
        onStepChangeRef.current(step.id, {startTime: dayjs().tz(timezoneRef.current, false)});
    };

    const cancelCountdown = () => {
        const targetStepId = capturedStepIdRef.current;
        if (targetStepId) {
            onStepChangeRef.current(targetStepId, {startTime: previousStartTimeRef.current});
        }
        clearCountdownState();
    };

    const stopCountdown = (capturedState: CountdownState) => {
        const targetStepId = capturedStepIdRef.current;
        if (!targetStepId) {
            console.log("Missing captured step id in CountdownState");
            clearCountdownState();
            return;
        }

        const targetStep = orderedStepsRef.current.find((s) => s.id === targetStepId);
        if (!targetStep) {
            console.log(`Failed to find protocol step id:${targetStepId}`);
            clearCountdownState();
            return;
        }

        completeCountdownForStep(targetStep, capturedState.counter);
    };

    return {
        countDownState,
        startCountdown,
        stopCountdown,
        cancelCountdown,
    };
};

